import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check, MapPin, Calendar, X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import { Reveal, ImageReveal } from "@/components/Reveal";
import MarqueeBand from "@/components/MarqueeBand";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import CinematicVideo from "@/components/CinematicVideo";
import { api } from "@/lib/api";
import { sizedImg } from "@/lib/images";
import { SEO_BASE } from "@/lib/seo";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState("loading");
  const [lightbox, setLightbox] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    setStatus("loading");
    api
      .get(`/projects/${slug}`)
      .then((r) => { setProject(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
    // Also load full list for prev/next navigation
    api.get("/projects").then((r) => setAllProjects(r.data || [])).catch(() => {});
    // Increment the view counter (fire-and-forget). We deduplicate per session
    // via sessionStorage so refreshing the page doesn't inflate the count.
    // Also remember the current project as "last viewed" so the quote form can
    // attribute the conversion to this project.
    try {
      sessionStorage.setItem("lastViewedProject", slug);
      const key = `viewed:${slug}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        api.post(`/projects/${slug}/view`).catch(() => {});
      }
    } catch { /* sessionStorage may be blocked in private mode */ }
  }, [slug]);

  // Block page scroll while lightbox is open + keyboard navigation (Esc / arrows)
  useEffect(() => {
    if (lightbox === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (!project || !project.gallery) return;
      const g = project.gallery;
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % g.length);
      else if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + g.length) % g.length);
    };
    window.addEventListener("keydown", onKey);
    // Also lock wheel/scroll events (Lenis smooth scroll would otherwise still fire)
    const blockWheel = (e) => e.preventDefault();
    window.addEventListener("wheel", blockWheel, { passive: false });
    window.addEventListener("touchmove", blockWheel, { passive: false });
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", blockWheel);
      window.removeEventListener("touchmove", blockWheel);
    };
  }, [lightbox, project]);

  useEffect(() => {
    if (lightbox === null || !project) return;
    const imgs = project.gallery && project.gallery.length ? project.gallery : [project.image];
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % imgs.length);
      else if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + imgs.length) % imgs.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightbox, project]);

  if (status === "loading") {
    return (
      <div data-testid="project-detail-loading" className="min-h-[70vh] grid place-items-center">
        <span className="font-mono text-sm uppercase tracking-widest text-muted-foreground">Caricamento…</span>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div data-testid="project-detail-notfound" className="min-h-[70vh] grid place-items-center text-center px-5">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Errore 404</p>
          <h1 className="font-head font-black text-4xl md:text-5xl tracking-tightest mb-6">Progetto non trovato</h1>
          <Link to="/progetti" data-testid="detail-back-to-list" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" /> Torna ai progetti
          </Link>
        </div>
      </div>
    );
  }

  const gallery = project.gallery && project.gallery.length ? project.gallery : [project.image];
  const captions = project.captions || [];
  const captionFor = (i) => captions[i] || project.title;

  return (
    <div data-testid="project-detail-page">
      <Helmet>
        <title>{`${project.title} — Greenhouse Solutions SPA`}</title>
        <meta name="description" content={project.intro ? project.intro.split("\n\n")[0] : project.description} />
        <link rel="canonical" href={`${SEO_BASE}/progetti/${project.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${project.title} — Greenhouse Solutions SPA`} />
        <meta property="og:description" content={project.intro ? project.intro.split("\n\n")[0] : project.description} />
        <meta property="og:image" content={project.image} />
        <meta property="og:url" content={`${SEO_BASE}/progetti/${project.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={project.image} />
      </Helmet>

      {/* HEADER */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 overflow-hidden bg-background">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="font-mono text-xs uppercase tracking-widest text-primary mb-8 flex flex-wrap items-center gap-3">
              <Link to="/" className="text-muted-foreground hover:text-primary">Home</Link>
              <span className="text-muted-foreground">/</span>
              <Link to="/progetti" className="text-muted-foreground hover:text-primary">Progetti</Link>
              <span className="text-muted-foreground">/ {project.title}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-block font-mono text-xs uppercase tracking-widest bg-primary text-primary-foreground px-3 py-1.5 mb-6">{project.category}</span>
          </Reveal>
          <h1 className="font-head font-black text-[1.8rem] min-[380px]:text-[2.2rem] sm:text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] max-w-4xl">
            {project.title}
          </h1>
          <Reveal delay={0.25}>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-sm text-muted-foreground uppercase tracking-widest">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" /> {project.location}</span>
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-primary" /> {project.year}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HERO IMAGE */}
      <section className="bg-background pb-20 md:pb-28">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          <ImageReveal>
            {project.wip ? (
              <div data-testid="project-wip-hero" className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9] min-h-[360px] sm:min-h-[400px] overflow-hidden border border-border bg-foreground text-background flex flex-col items-center justify-center text-center px-6">
                <div className="absolute inset-0 grid-lines opacity-15" />
                <span className="relative font-mono text-xs uppercase tracking-widest text-primary mb-4">// Cantiere in corso</span>
                <h2 className="relative font-head font-black text-3xl sm:text-4xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95]">
                  Work in Progress
                </h2>
                <p className="relative mt-4 md:mt-5 text-sm md:text-base text-background/70 max-w-lg">Le foto di questo cantiere saranno disponibili a breve, non appena i lavori saranno completati.</p>
              </div>
            ) : (
              <div className="relative aspect-[21/9] overflow-hidden border border-border">
                <img src={sizedImg(project.image, 1600)} alt={project.title} loading="eager" decoding="async" className="w-full h-full object-cover" />
              </div>
            )}
          </ImageReveal>
        </div>
      </section>

      <MarqueeBand />

      {/* INTRO + INTERVENTIONS */}
      <section className="py-24 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">// Il progetto</p>
              <div className="space-y-5 text-lg md:text-xl text-muted-foreground leading-relaxed">
                {(project.intro || project.description).split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <Link
                to="/preventivo"
                data-testid="detail-cta-preventivo"
                className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-widest px-8 py-5 hover:bg-foreground hover:text-background transition-colors"
              >
                Richiedi un preventivo <ArrowUpRight className="w-5 h-5" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <h2 className="font-head font-black text-2xl md:text-3xl tracking-tightest mb-8">Interventi eseguiti</h2>
              <ul className="space-y-px bg-border border border-border">
                {project.interventions.map((it, i) => (
                  <li key={i} data-testid={`detail-intervention-${i}`} className="flex items-start gap-4 bg-card px-6 py-5">
                    <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="font-medium">{it}</span>
                  </li>
                ))}
              </ul>

              {project.services && project.services.length > 0 && (
                <div data-testid="detail-services" className="mt-12">
                  <p className="font-mono text-xs uppercase tracking-widest text-primary mb-5">// Servizi utilizzati</p>
                  <div className="flex flex-wrap gap-3">
                    {project.services.map((s, i) => (
                      <Link
                        key={i}
                        to={s.to}
                        data-testid={`detail-service-${i}`}
                        className="group inline-flex items-center gap-2 border border-border bg-card px-5 py-3 font-mono text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors"
                      >
                        {s.label}
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </div>
      </section>

      {/* VIDEO */}
      {project.video && (
        <section data-testid="detail-video-section" className="pb-24 md:pb-32 bg-background">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">// Video del cantiere</p>
              <h2 className="font-head font-black text-2xl md:text-3xl tracking-tightest mb-8">
                Guarda l'intervento
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <CinematicVideo
                src={project.video}
                poster={project.image}
                testId="detail-video-player"
                ariaLabel={`Video del cantiere ${project.title}`}
              />
            </Reveal>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {!project.wip && project.gallery && project.gallery.length > 0 && (
      <section className="pb-24 md:pb-32 bg-background">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10">
          {project.before_after && project.before_after.before && project.before_after.after && (
            <div data-testid="detail-before-after" className="mb-16 md:mb-24">
              <Reveal>
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">// Prima / Dopo</p>
                <h2 className="font-head font-black text-2xl md:text-3xl tracking-tightest mb-8">
                  {project.before_after.label || "Il confronto"}
                </h2>
              </Reveal>
              <BeforeAfterSlider
                before={project.before_after.before}
                after={project.before_after.after}
                beforeLabel={project.before_after.before_label || "PRIMA"}
                afterLabel={project.before_after.after_label || "DOPO"}
                caption="Trascina lo slider per confrontare"
              />
            </div>
          )}

          <Reveal>
            <h2 className="font-head font-black text-2xl md:text-3xl tracking-tightest mb-10">Galleria</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {gallery.map((img, i) => (
              <motion.button
                key={i}
                type="button"
                onClick={() => setLightbox(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`detail-gallery-${i}`}
                aria-label={`Apri foto ${i + 1} a schermo intero`}
                className={`relative overflow-hidden border border-border group cursor-pointer ${i === 0 ? "sm:col-span-2 lg:col-span-2 aspect-[16/9]" : "aspect-[4/3]"}`}
              >
                <img src={sizedImg(img, 1000)} alt={captionFor(i)} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-start justify-end p-5 text-left">
                  <Expand className="w-6 h-6 text-white mb-auto ml-auto" />
                  <span className="font-mono text-xs uppercase tracking-widest text-white">{captionFor(i)}</span>
                </div>
              </motion.button>
            ))}
          </div>

          <div className="mt-16">
            <Link to="/progetti" data-testid="detail-back-to-list" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 hover:gap-3 transition-all">
              <ArrowLeft className="w-4 h-4" /> Tutti i progetti
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Back link fallback per WIP (galleria nascosta) */}
      {project.wip && (
        <section className="pb-16 md:pb-20 bg-background">
          <div className="max-w-[1500px] mx-auto px-5 md:px-10">
            <Link to="/progetti" data-testid="detail-back-to-list" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-b-2 border-primary pb-1 md:hover:gap-3 transition-all">
              <ArrowLeft className="w-4 h-4" /> Tutti i progetti
            </Link>
          </div>
        </section>
      )}

      {/* PROJECT NAVIGATION — prev / next */}
      {allProjects.length > 1 && (() => {
        const idx = allProjects.findIndex((p) => p.slug === slug);
        if (idx === -1) return null;
        const total = allProjects.length;
        const prev = allProjects[(idx - 1 + total) % total];
        const next = allProjects[(idx + 1) % total];
        return (
          <section className="pb-24 md:pb-32 bg-background border-t border-border" data-testid="project-nav">
            <div className="max-w-[1500px] mx-auto px-5 md:px-10 pt-14 md:pt-20">
              <div className="flex items-center justify-between mb-8 md:mb-12">
                <p className="font-mono text-xs uppercase tracking-widest text-primary">// Continua il tour</p>
                <Link to="/progetti" data-testid="project-nav-all" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground md:hover:text-foreground transition-colors">
                  <span>Tutti i progetti</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                {[
                  { p: prev, label: "Progetto precedente", dir: "left", testid: "project-nav-prev" },
                  { p: next, label: "Progetto successivo", dir: "right", testid: "project-nav-next" },
                ].map(({ p, label, dir, testid }) => (
                  <Link
                    key={testid}
                    to={`/progetti/${p.slug}`}
                    data-testid={testid}
                    className={`group relative overflow-hidden border border-border bg-card ${dir === "right" ? "md:text-right" : ""}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={sizedImg(p.image, 900)}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 md:group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <p className={`font-mono text-[10px] md:text-xs uppercase tracking-widest text-primary flex items-center gap-2 ${dir === "right" ? "md:justify-end" : ""}`}>
                        {dir === "left" && <ChevronLeft className="w-3.5 h-3.5" />}
                        <span>{label}</span>
                        {dir === "right" && <ChevronRight className="w-3.5 h-3.5" />}
                      </p>
                      <p className="mt-2 font-head font-black text-xl md:text-2xl tracking-tightest leading-tight">
                        {p.title}
                      </p>
                      <p className="mt-1 font-mono text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">
                        {p.location}{p.year ? ` · ${p.year}` : ""}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* LIGHTBOX */}
      {lightbox !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none overscroll-contain"
          style={{ overscrollBehavior: "contain" }}
          onClick={() => setLightbox(null)}
          onWheel={(e) => e.preventDefault()}
          onTouchStart={(e) => {
            const t = e.touches[0];
            touchStartX.current = t.clientX;
            touchStartY.current = t.clientY;
          }}
          onTouchMove={(e) => {
            // Prevent underlying page from scrolling while swiping the lightbox
            if (touchStartX.current !== null) e.preventDefault();
          }}
          onTouchEnd={(e) => {
            if (touchStartX.current == null) return;
            const t = e.changedTouches[0];
            const dx = t.clientX - touchStartX.current;
            const dy = t.clientY - touchStartY.current;
            touchStartX.current = null;
            touchStartY.current = null;
            const SWIPE_THRESHOLD = 45;
            // Only trigger if horizontal swipe dominates vertical
            if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.2) {
              if (dx < 0) setLightbox((v) => (v + 1) % gallery.length);
              else setLightbox((v) => (v - 1 + gallery.length) % gallery.length);
            }
          }}
        >
          <button
            type="button"
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Chiudi"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 grid place-items-center border border-white/20 text-white hover:bg-white hover:text-black transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            type="button"
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v - 1 + gallery.length) % gallery.length); }}
            aria-label="Foto precedente"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center border border-white/20 text-white hover:bg-white hover:text-black transition-colors z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            type="button"
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v + 1) % gallery.length); }}
            aria-label="Foto successiva"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center border border-white/20 text-white hover:bg-white hover:text-black transition-colors z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <motion.img
            key={lightbox}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            src={sizedImg(gallery[lightbox], 1800)}
            alt={captionFor(lightbox)}
            data-testid="lightbox-image"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[80vh] object-contain border border-white/10"
          />

          <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2 px-6 text-center">
            <span data-testid="lightbox-caption" className="font-head font-bold text-base md:text-lg text-white">
              {captionFor(lightbox)}
            </span>
            <span className="font-mono text-xs uppercase tracking-widest text-white/60">
              {lightbox + 1} / {gallery.length}
            </span>
          </div>
        </motion.div>
      )}
    </div>
  );
}
